// @givmo/board — the quorum figures beside a motion.
//
// ══ THIS COMPONENT IS THE SURFACE THE §3.7 LAW IS VISIBLE ON ═══════════════════════════════════
//
// It renders ARITHMETIC PLUS THE ORGANIZATION'S OWN RECORDED PARAMETER, and never a conclusion:
//
//     6 of 9 voting directors present · your recorded quorum: majority (Art. III §4)
//
// What it must never render — and what §3.4's terminology rail fails the build over, in copy AND in
// identifier names — is "quorum met", "properly noticed", or any phrasing that says the board was
// able to act. Whether a meeting had quorum is a governance conclusion; the module counts and cites,
// and the reader concludes.
//
// ⚠ **A RECUSED DIRECTOR IS STILL IN BOTH NUMBERS.** Recusal is a STATE (absent from the debate and
// the vote), not a deduction from the room. The counts come off the wire captured at the instant of
// the vote and are rendered verbatim — see `in-meeting-decode.ts`'s header for why there is no
// arithmetic anywhere on this path, and `in-meeting-decode.test.ts` for the rail that keeps it that
// way. If a recused seat is being shown, it is shown as a LABEL beside a name, never as a number
// beside these figures.
//
// The backend's own rendered `statement` LEADS when it is present — the same rule `notice-block.tsx`
// follows for the notice sentence: where the backend wrote the sentence, that sentence is what a
// reader sees, and this app does not paraphrase it into a second wording that could drift.

import type { BoardQuorumOut } from '@givmo/api-client';
import { Badge } from '@givmo/design-system';
import {
  hasQuorumFigures,
  isShortOfRecordedQuorum,
  presenceSentence,
  recordedQuorumSentence,
  shortfallSeats,
} from './in-meeting-decode';

export interface QuorumBlockProps {
  quorum: BoardQuorumOut | null | undefined;
  testId: string;
}

export function QuorumBlock({ quorum, testId }: QuorumBlockProps) {
  // Nothing recorded is nothing shown. A motion with no vote has no quorum-at-vote, and an empty
  // block with dashes in it would imply figures exist that are being withheld.
  if (!hasQuorumFigures(quorum)) return null;

  const backendStatement = (quorum?.statement ?? '').trim();
  const presence = presenceSentence(quorum);
  const recorded = recordedQuorumSentence(quorum);
  const shortfall = shortfallSeats(quorum);

  return (
    <div className="flex flex-col gap-6" data-testid={testId}>
      {backendStatement.length > 0 ? (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          data-testid={`${testId}-statement`}
        >
          {backendStatement}
        </p>
      ) : null}

      {presence !== null || recorded !== null ? (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          data-testid={`${testId}-figures`}
        >
          {/* Joined with a middot rather than a sentence connective: the two halves are a count and a
              recorded parameter, and any connective ("so", "which means", "therefore") would be the
              product drawing the conclusion between them. */}
          {[presence, recorded].filter((part) => part !== null).join(' · ')}
        </p>
      ) : null}

      {shortfall !== null ? (
        <div className="flex flex-wrap items-center gap-8" data-testid={`${testId}-shortfall`}>
          <Badge tone="warning">
            {shortfall === 1 ? '1 seat short' : `${shortfall} seats short`}
          </Badge>
          <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {/* Says what the figures are, and stops. It does not say the motion is ineffective, that
                the board could not act, or that anything needs to be redone — every one of those is
                the §3.2 Band-3 conclusion the organization's own counsel draws, not the product. */}
            Fewer seats were recorded present than the quorum you recorded for this board. The
            motion and its vote are recorded as they were.
          </span>
        </div>
      ) : isShortOfRecordedQuorum(quorum) ? (
        // Flagged by the backend, but with no seat count behind it — a FRACTION basis
        // ("two-thirds of the directors"), which yields no required number of seats without a
        // rounding convention that instruments disagree about. The flag is shown; no figure is
        // invented to sit beside it.
        <div className="flex flex-wrap items-center gap-8" data-testid={`${testId}-shortfall`}>
          <Badge tone="warning">Short of the quorum you recorded</Badge>
          <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            Your recorded quorum is a fraction, so this is not stated as a number of seats.
          </span>
        </div>
      ) : null}
    </div>
  );
}
